import { DeviceAlertMetricName } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { blue, lightGreen, orange, red, yellow } from '@mui/material/colors';

export type DeviceAlertMetricNameAttributes = {
  value: DeviceAlertMetricName;
  label: string;
  color: string;
};

type AttributesMap = Record<DeviceAlertMetricName, DeviceAlertMetricNameAttributes>;

const getAttributesMap = (i18n: I18n): AttributesMap => ({
  [DeviceAlertMetricName.Flow]: {
    value: DeviceAlertMetricName.Flow,
    label: t(i18n)`Caudal`,
    color: blue[700]
  },
  [DeviceAlertMetricName.Level]: {
    value: DeviceAlertMetricName.Level,
    label: t(i18n)`Nivel`,
    color: lightGreen[700],
  },
  [DeviceAlertMetricName.Intruder]: {
    value: DeviceAlertMetricName.Intruder,
    label: t(i18n)`Intruso`,
    color: orange[700],
  },
  [DeviceAlertMetricName.BatterySoc]: {
    value: DeviceAlertMetricName.BatterySoc,
    label: t(i18n)`Batería`,
    color: yellow[700],
  },
  [DeviceAlertMetricName.SensorDistance]: {
    value: DeviceAlertMetricName.SensorDistance,
    label: t(i18n)`Distancia de sensor`,
    color: red[700],
  },
  [DeviceAlertMetricName.Temperature]: {
    value: DeviceAlertMetricName.Temperature,
    label: t(i18n)`Temperatura`,
    color: red[700],
  },
  [DeviceAlertMetricName.Conductivity]: {
    value: DeviceAlertMetricName.Conductivity,
    label: t(i18n)`Conductividad`,
    color: red[700],
  },
  [DeviceAlertMetricName.DissolvedOxygen]: {
    value: DeviceAlertMetricName.DissolvedOxygen,
    label: t(i18n)`Oxígeno disuelto`,
    color: red[700],
  },
  [DeviceAlertMetricName.DissolvedOxygenSaturation]: {
    value: DeviceAlertMetricName.DissolvedOxygenSaturation,
    label: t(i18n)`Saturación de oxígeno disuelto`,
    color: red[700],
  },
  [DeviceAlertMetricName.Orp]: {
    value: DeviceAlertMetricName.Orp,
    label: t(i18n)`Orp`,
    color: red[700],
  },
  [DeviceAlertMetricName.Ph]: {
    value: DeviceAlertMetricName.Ph,
    label: t(i18n)`Ph`,
    color: red[700],
  },
  [DeviceAlertMetricName.Velocity]: {
    value: DeviceAlertMetricName.Velocity,
    label: t(i18n)`Velocidad`,
    color: red[700],
  },
  [DeviceAlertMetricName.TurbidityFnu]: {
    value: DeviceAlertMetricName.TurbidityFnu,
    label: t(i18n)`Turbiedad fnu`,
    color: red[700],
  },
  [DeviceAlertMetricName.TurbidityNtu]: {
    value: DeviceAlertMetricName.TurbidityNtu,
    label: t(i18n)`Turbiedad`,
    color: red[700],
  },
});

/**
 * Retrieves the attributes associated with a specific `DeviceAlertMetricName` key.
 *
 * @param key - The `DeviceAlertMetricName` key for which attributes are requested.
 * @param i18n - The i18n instance used for localization.
 * @returns The attributes corresponding to the provided key.
 * @throws If the key is not found in the attributes map, an error is thrown.
 */
export const getDeviceAlertMetricAttributes = (
  key: DeviceAlertMetricName,
  i18n: I18n,
): DeviceAlertMetricNameAttributes => {
  const attributesMap = getAttributesMap(i18n);
  if (!(key in attributesMap)) {
    throw new Error(`Key ${key} not found in the DeviceAlertMetricName attributes map`);
  }
  return attributesMap[key];
};

/**
 * Retrieves a list of all attributes defined in the attributes map, sorted by their precedence.
 *
 * @param i18n - The i18n instance used for localization.
 * @returns An array of all `DeviceAlertMetricAttributes`.
 */
export const getAllDeviceAlertMetricAttributes = (
  i18n: I18n,
): DeviceAlertMetricNameAttributes[] => {
  const attributesMap = getAttributesMap(i18n);
  return Object.values(attributesMap);
};

export const isDeviceAlertMetricName = (key: string): key is DeviceAlertMetricName => {
  const metricNames = Object.values(DeviceAlertMetricName);
  return metricNames.includes(key as (typeof metricNames)[number]);
};
